/**
 * Setup milestone definitions — progress on dashboard; work happens under Setup hubs.
 */
export type SetupMilestoneId =
  | 'tenant_created'
  | 'salesforce_connected'
  | 'authentication_working'
  | 'call_center_configured'
  | 'queue_selected'
  | 'omni_flow_selected'
  | 'telephony_connected'
  | 'phone_numbers_synced'
  | 'agents_assigned'
  | 'validation_passed'
  | 'ready_to_go_live';

export type SetupMilestoneDefinition = {
  id: SetupMilestoneId;
  label: string;
  description: string;
  /** Where to complete this milestone in the main app */
  configureHref?: string;
  configureLabel?: string;
};

export const SETUP_MILESTONES: SetupMilestoneDefinition[] = [
  {
    id: 'tenant_created',
    label: 'Organization created',
    description: 'Your organization account is registered.',
  },
  {
    id: 'salesforce_connected',
    label: 'Salesforce connected',
    description: 'Register a Salesforce Org with SCRT URL and Salesforce Org ID.',
    configureHref: '/salesforce',
    configureLabel: 'Configure Salesforce',
  },
  {
    id: 'authentication_working',
    label: 'Authentication working',
    description: 'SCRT and REST JWT credentials saved and tested.',
    configureHref: '/salesforce',
    configureLabel: 'Configure auth',
  },
  {
    id: 'call_center_configured',
    label: 'Contact Center configured',
    description: 'Contact Center API name matches BYOT center.',
    configureHref: '/salesforce',
    configureLabel: 'Configure routing',
  },
  {
    id: 'queue_selected',
    label: 'Queue selected',
    description: 'Default voice queue assigned for routing.',
    configureHref: '/salesforce',
    configureLabel: 'Configure routing',
  },
  {
    id: 'omni_flow_selected',
    label: 'Omni Flow selected',
    description: 'Omni voice flow configured for inbound calls.',
    configureHref: '/salesforce',
    configureLabel: 'Configure routing',
  },
  {
    id: 'telephony_connected',
    label: 'Telephony connected',
    description: 'Twilio or Amazon Connect connection with credentials.',
    configureHref: '/telephony',
    configureLabel: 'Configure Telephony',
  },
  {
    id: 'phone_numbers_synced',
    label: 'Phone Numbers synced',
    description: 'At least one active phone number provisioned.',
    configureHref: '/telephony/phone-numbers',
    configureLabel: 'Manage Phone Numbers',
  },
  {
    id: 'agents_assigned',
    label: 'Salesforce Agents assigned',
    description: 'Salesforce Agents synced and enabled for this Salesforce Org.',
    configureHref: '/salesforce',
    configureLabel: 'Manage Salesforce Agents',
  },
  {
    id: 'validation_passed',
    label: 'Validation passed',
    description: 'Critical validation checks pass.',
    configureHref: '/validation',
    configureLabel: 'Run validation',
  },
  {
    id: 'ready_to_go_live',
    label: 'Ready to Go Live',
    description: 'Setup complete — ready for production traffic.',
    configureHref: '/validation',
    configureLabel: 'Review & go live',
  },
];

export const SETUP_MILESTONE_COUNT = SETUP_MILESTONES.length;

export function getMilestoneDefinition(id: SetupMilestoneId | string) {
  return SETUP_MILESTONES.find((m) => m.id === id);
}

export function getFirstIncompleteConfigureHref(
  milestones: Array<{ id: string; complete: boolean; configureHref?: string | null }>
): string {
  const incomplete = milestones.find((m) => !m.complete && m.configureHref);
  return incomplete?.configureHref ?? '/salesforce';
}
